import { UploadData } from '../../../../store/uploadStore';
import { UploadValidationStatus } from '../../../../consts/uploadValidationStatus';
import { IndividualValidationStatus } from '../../../../consts/individualValidationStatus';
import { getIndividualValidationStatus } from './individualValidation';
import { ValidationCheck } from '../../../../consts/validationCheck';
import { Blue10, Blue80, Green10, Green90, Red10, Red80 } from '@traceair/tds';
import { getFilteredValidations } from './getFilteredValidations';

export type FlaggedValidations = {
  [key in ValidationCheck]?: {
    count: number;
    names: string[];
  };
};

export const getUploadValidationCount = (upload: UploadData | undefined) => {
  if (!upload) return 0;

  const uploadedCount =
    upload.upload?.photos.reduce((acc, photo) => acc + (photo.validation && photo.uploaded ? 1 : 0), 0) ?? 0;
  const queuedCount = upload.queue.reduce((acc, photo) => acc + (photo.validation ? 1 : 0), 0);

  return uploadedCount + queuedCount;
};

export const getUploadValidationStatus = (upload: UploadData | undefined, count?: number) => {
  if (!upload) return UploadValidationStatus.InProcess;

  let hasWarnings = false;
  let hasErrors = false;

  const combinedValidations = getFilteredValidations(upload);

  combinedValidations.forEach(({ validation }) => {
    if (validation) {
      Object.entries(validation).forEach(([validation, result]) => {
        const status = getIndividualValidationStatus(validation as ValidationCheck, result[0]);
        if (status === IndividualValidationStatus.Warning) {
          hasWarnings = true;
        }
        if (status === IndividualValidationStatus.Error) {
          hasErrors = true;
        }
      });
    }
  });

  if (hasErrors) {
    return UploadValidationStatus.HasErrors;
  }

  if (hasWarnings) {
    return UploadValidationStatus.HasWarnings;
  }

  return count === upload.overview.photosCount.all ? UploadValidationStatus.Success : UploadValidationStatus.InProcess;
};

export const getUploadValidationsWithIssues = (upload: UploadData | undefined) => {
  if (!upload) return {};

  const combinedValidations = getFilteredValidations(upload);

  const flagged: FlaggedValidations = {};

  for (const key of Object.values(ValidationCheck)) {
    for (const { validation, photo } of combinedValidations) {
      if (validation && validation[key] && !validation[key]![0]) {
        if (!flagged[key]) {
          flagged[key] = { count: 0, names: [] };
        }

        flagged[key]!.count++;
        flagged[key]!.names.push(photo);
      }
    }
  }

  return flagged;
};

export const getUploadValidationStatusColors = (status: UploadValidationStatus) =>
  ({
    [UploadValidationStatus.InProcess]: { primary: Blue80, secondary: Blue10 },
    [UploadValidationStatus.Success]: { primary: Green90, secondary: Green10 },
    [UploadValidationStatus.HasErrors]: { primary: Red80, secondary: Red10 },
    [UploadValidationStatus.HasWarnings]: { primary: Green90, secondary: Green10 },
  })[status];
